/**
 * Keeping the media session through a short signaling drop.
 *
 * The SFU session used to live exactly as long as the room channel was joined:
 * `useSfuJoin` was handed `channelJoined ? roomId : null`. So every signaling
 * blip — a Wi-Fi hand-off, three seconds of lost network — tore the whole media
 * session down, told the orchestrator this peer had LEFT, and rebuilt it from
 * nothing once the channel was back: a new orchestrator join, a new control
 * socket, both transports, the TURN fetch, then capture and produce again.
 *
 * None of that was needed. The server keeps the session for exactly this case:
 * signaling waits 30 s before ending a dropped participant's media (Departures,
 * `@default_grace_ms`), a rejoin inside that window cancels it, and other
 * participants only drop our tiles on `media:peer_left`, which that end sends.
 * The SFU's own control socket and transports recover on their own (socket
 * reconnect, ICE watchdog). The client's teardown — and its `/leave` — was the
 * only thing turning a blip into an outage. On a slow laptop the rebuild alone
 * took 5–15 s, and a `/leave` sent while offline could land after the rejoin
 * and delete the new session.
 *
 * So the session now outlives the channel by MEDIA_HOLD_MS. Back inside it, the
 * same session simply carries on. Not back by then, it is torn down as before,
 * and the channel's return builds a fresh one.
 *
 * Kept pure — no React, timers injectable — so the rules can be tested
 * directly; `useMediaSessionHold` wires them to the meeting.
 */

/**
 * How long the media session outlives a dropped room channel.
 *
 * Under signaling's 30 s departure grace, so the client — not a server-side
 * `/leave` racing a rejoin — decides when a held session ends.
 */
export const MEDIA_HOLD_MS = 25_000

export type MediaSessionHold = {
  /** Call on every change of the room channel's joined state. */
  update: (channelJoined: boolean) => void
  /** Stop the hold timer; no further onChange. */
  dispose: () => void
}

type Timers = {
  setTimer: (fn: () => void, ms: number) => unknown
  clearTimer: (handle: unknown) => void
}

const realTimers: Timers = {
  setTimer: (fn, ms) => setTimeout(fn, ms),
  clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
}

/**
 * Whether the media session should exist, given the channel's joined state over
 * time. `onChange(true)` once the channel first joins; `onChange(false)` only
 * when the channel has stayed down for `holdMs`.
 */
export function createMediaSessionHold(opts: {
  onChange: (active: boolean) => void
  holdMs?: number
  timers?: Timers
}): MediaSessionHold {
  const holdMs = opts.holdMs ?? MEDIA_HOLD_MS
  const { setTimer, clearTimer } = opts.timers ?? realTimers
  let active = false
  let timer: unknown = null
  let disposed = false

  const cancelHold = () => {
    if (timer !== null) clearTimer(timer)
    timer = null
  }

  return {
    update(channelJoined) {
      if (disposed) return
      if (channelJoined) {
        cancelHold()
        if (!active) {
          active = true
          opts.onChange(true)
        }
        return
      }
      // Dropped. Nothing to hold before the first join, and one hold per drop:
      // a repeated `false` must not push the deadline out.
      if (!active || timer !== null) return
      timer = setTimer(() => {
        timer = null
        if (disposed) return
        active = false
        opts.onChange(false)
      }, holdMs)
    },
    dispose() {
      disposed = true
      cancelHold()
    },
  }
}
